import React, {useState, useEffect, useCallback, useRef} from 'react';
import Modal from '../../../Presentation/components/ui/Modal';
import {AppText, AppCaption} from '../../../Presentation/components/ui/Typography';
import Button from '../../../Presentation/components/ui/Button';
import {Package, MapPin, Hash, Calendar, User, History, RefreshCw, Receipt} from 'lucide-react';
import DateTimeDisplay from '../../../Presentation/components/ui/DateTimeDisplay';
import {useInventory} from '../hooks/useInventory';
import {getMovementTypeLabel, getMovementIcon} from '../utils/movementTypeLabels';
import {INVENTORY_ERROR_BOX_CLASS, INVENTORY_LOADING_TEXT} from '../ui/inventoryUi';
import type {SerialItem} from './SerialsTrackingTable';
import type {TimelineItem, SerialLastSale} from '../models/inventory.model';

interface SerialDetailsModalProps {
  open: boolean;
  serial: SerialItem | null;
  onClose: () => void;
  onViewInMovements: (serial: SerialItem) => void;
}

const SerialDetailsModal: React.FC<SerialDetailsModalProps> = ({
  open,
  serial,
  onClose,
  onViewInMovements
}) => {
  const {getSerialTimeline, getSerialDetails} = useInventory();
  const getSerialTimelineRef = useRef(getSerialTimeline);
  const getSerialDetailsRef = useRef(getSerialDetails);
  getSerialTimelineRef.current = getSerialTimeline;
  getSerialDetailsRef.current = getSerialDetails;

  const [details, setDetails] = useState<{ lastSale?: SerialLastSale } | null>(null);
  const [timeline, setTimeline] = useState<TimelineItem[]>([]);
  const [timelinePage, setTimelinePage] = useState(1);
  const [timelineHasMore, setTimelineHasMore] = useState(false);
  const [timelineTotal, setTimelineTotal] = useState(0);
  const [timelineLoading, setTimelineLoading] = useState(false);
  const [timelineLoadMore, setTimelineLoadMore] = useState(false);
  const [timelineError, setTimelineError] = useState<string | null>(null);
  const [timelineRetry, setTimelineRetry] = useState(0);

  const loadMoreTimeline = useCallback(() => {
    if (!serial?.serialNumber || timelineLoadMore || !timelineHasMore) return;
    setTimelineLoadMore(true);
    setTimelineError(null);
    const nextPage = timelinePage + 1;
    getSerialTimelineRef.current(serial.serialNumber, nextPage, 20)
      .then(response => {
        const items = response?.timeline ?? [];
        const pag = response?.pagination;
        setTimeline(prev => [...prev, ...items]);
        setTimelinePage(nextPage);
        const total = pag?.total ?? 0;
        setTimelineTotal(total);
        setTimelineHasMore(total > 0 && nextPage * 20 < total);
      })
      .catch(err => {
        const msg = err instanceof Error ? err.message : 'فشل في جلب الخط الزمني';
        setTimelineError(msg);
      })
      .finally(() => setTimelineLoadMore(false));
  }, [serial?.serialNumber, timelinePage, timelineHasMore, timelineLoadMore]);

  useEffect(() => {
    const serialNumber = serial?.serialNumber;
    if (!open || !serialNumber) {
      setDetails(null);
      setTimeline(prev => (prev.length > 0 ? [] : prev));
      setTimelineError(prev => (prev !== null ? null : prev));
      setTimelinePage(1);
      setTimelineHasMore(false);
      return;
    }
    let cancelled = false;
    setTimelineLoading(true);
    setTimelineError(null);
    getSerialTimelineRef.current(serialNumber, 1, 20)
      .then(response => {
        if (cancelled) return;
        const items = response?.timeline ?? [];
        const pag = response?.pagination;
        setTimeline(items);
        setTimelinePage(1);
        const total = pag?.total ?? items.length;
        setTimelineTotal(total);
        setTimelineHasMore(total > 0 && items.length >= 20 && items.length < total);
      })
      .catch(err => {
        if (!cancelled) {
          const msg = err instanceof Error ? err.message : 'فشل في جلب الخط الزمني';
          setTimelineError(msg);
          setTimeline([]);
        }
      })
      .finally(() => {
        if (!cancelled) setTimelineLoading(false);
      });
    return () => { cancelled = true; };
  }, [open, serial?.serialNumber, timelineRetry]);

  useEffect(() => {
    const serialNumber = serial?.serialNumber;
    if (!open || !serialNumber) {
      setDetails(null);
      return;
    }
    let cancelled = false;
    getSerialDetailsRef.current(serialNumber)
      .then(d => {
        if (!cancelled && d?.lastSale) setDetails({lastSale: d.lastSale});
      })
      .catch(() => { /* ignore - fallback to row data */ });
    return () => { cancelled = true; };
  }, [open, serial?.serialNumber]);

  if (!serial) return null;

  const statusConfig = {
    available: {label: 'متاح', className: 'bg-green-100 text-green-800'},
    assigned: {label: 'مخصص للمسوق', className: 'bg-brand-100 text-brand-800'},
    sold: {label: 'مباع', className: 'bg-purple-100 text-purple-800'}
  };
  const config = statusConfig[serial.status] || {label: serial.statusText, className: 'bg-gray-100 text-gray-800'};

  /** Prefer type_ar when Arabic/human readable; fallback to getMovementTypeLabel */
  const getTypeLabel = (item: TimelineItem): string => {
    const typeAr = item.typeAr?.trim();
    if (typeAr && (/\p{Script=Arabic}/u.test(typeAr) || !/^[a-z0-9_]+$/.test(typeAr))) {
      return typeAr;
    }
    return getMovementTypeLabel(item.type).label;
  };

  const hasFromOrTo = (item: TimelineItem) => {
    const from = item.fromLocationName?.trim();
    const to = item.toLocationName?.trim();
    return (from && from !== '-') || (to && to !== '-');
  };

  return (
    <Modal open={open} onClose={onClose} title="تفاصيل السيريال" width="max-w-lg">
      <div className="space-y-5">
        {/* Serial number */}
        <div className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg">
          <Hash className="w-5 h-5 text-brand-600" />
          <div>
            <AppCaption className="text-gray-500">الرقم التسلسلي</AppCaption>
            <AppText className="font-mono font-bold text-gray-900" dir="ltr">
              {serial.serialNumber}
            </AppText>
          </div>
        </div>

        {/* Product */}
        <div className="flex items-center gap-3">
          <Package className="w-5 h-5 text-brand-600 flex-shrink-0" />
          <div>
            <AppCaption className="text-gray-500">المنتج</AppCaption>
            <AppText className="font-medium">{serial.productName}</AppText>
          </div>
        </div>

        {/* Status */}
        <div>
          <AppCaption className="text-gray-500 mb-1">الحالة</AppCaption>
          <span className={`px-2 py-1 rounded text-sm font-medium ${config.className}`}>
            {config.label}
          </span>
        </div>

        {/* Location */}
        <div className="flex items-center gap-3">
          <MapPin className="w-5 h-5 text-green-600 flex-shrink-0" />
          <div>
            <AppCaption className="text-gray-500">الموقع</AppCaption>
            <AppText className="font-medium">{serial.locationName}</AppText>
          </div>
        </div>

        {/* Assigned to */}
        {serial.assignedTo && (
          <div className="flex items-center gap-3">
            <User className="w-5 h-5 text-orange-600 flex-shrink-0" />
            <div>
              <AppCaption className="text-gray-500">مخصص لـ</AppCaption>
              <AppText className="font-medium">{serial.assignedTo}</AppText>
            </div>
          </div>
        )}

        {/* Created at */}
        <div className="flex items-center gap-3">
          <Calendar className="w-5 h-5 text-purple-600 flex-shrink-0" />
          <div>
            <AppCaption className="text-gray-500">تاريخ الإنشاء</AppCaption>
            <DateTimeDisplay
              dateString={serial.createdAt}
              dateClassName="text-sm font-medium text-gray-900"
              timeClassName="text-xs text-gray-500"
            />
          </div>
        </div>

        {/* Last movement */}
        {serial.lastMovement && (
          <div>
            <AppCaption className="text-gray-500 mb-1">آخر حركة</AppCaption>
            <DateTimeDisplay
              dateString={serial.lastMovement}
              dateClassName="text-sm font-medium text-gray-900"
              timeClassName="text-xs text-gray-500"
            />
          </div>
        )}

        {/* Last sale (from API) */}
        {details?.lastSale && (
          <div className="p-3 rounded-lg border border-gray-200 bg-purple-50/50">
            <div className="flex items-center gap-2 mb-2">
              <Receipt className="w-5 h-5 text-purple-600" />
              <AppText className="font-semibold">آخر بيع</AppText>
            </div>
            <div className="space-y-1 text-sm">
              {details.lastSale.invoiceNumber && (
                <div><AppCaption className="text-gray-500">رقم الفاتورة:</AppCaption> {details.lastSale.invoiceNumber}</div>
              )}
              {details.lastSale.storeName && (
                <div><AppCaption className="text-gray-500">المتجر:</AppCaption> {details.lastSale.storeName}</div>
              )}
              {details.lastSale.marketerName && (
                <div><AppCaption className="text-gray-500">المسوق:</AppCaption> {details.lastSale.marketerName}</div>
              )}
              {details.lastSale.saleDate && (
                <div>
                  <AppCaption className="text-gray-500">تاريخ البيع:</AppCaption>{' '}
                  <DateTimeDisplay
                    dateString={details.lastSale.saleDate}
                    dateClassName="text-sm font-medium"
                    timeClassName="text-xs text-gray-500"
                  />
                </div>
              )}
            </div>
          </div>
        )}

        {/* Timeline section */}
        <div className="pt-4 border-t border-gray-200">
          <div className="flex items-center gap-2 mb-3">
            <History className="w-5 h-5 text-brand-600" />
            <AppText className="font-semibold">الخط الزمني للسيريال</AppText>
          </div>
          {timelineLoading && (
            <div className="space-y-3">
              <div className="text-sm text-gray-500">{INVENTORY_LOADING_TEXT}</div>
              <div className="animate-pulse space-y-3">
                {[1, 2, 3].map((i) => (
                  <div key={i} className="h-16 bg-gray-100 rounded-lg" />
                ))}
              </div>
            </div>
          )}
          {!timelineLoading && timelineError && (
            <div className={INVENTORY_ERROR_BOX_CLASS}>
              <div className="text-red-700 text-sm mb-3">{timelineError}</div>
              <Button variant="outline" size="small" onClick={() => setTimelineRetry(c => c + 1)} className="flex items-center gap-2">
                <RefreshCw className="w-4 h-4" />
                إعادة المحاولة
              </Button>
            </div>
          )}
          {!timelineLoading && !timelineError && (
            <>
              <div className="text-sm text-gray-500 mb-3">
                عدد الحركات: {timeline.length}{timelineTotal > 0 ? ` من ${timelineTotal}` : ''}
              </div>
              {timeline.length === 0 ? (
                <div className="text-sm text-gray-500 py-4 text-center rounded-lg bg-gray-50">
                  لا توجد حركات مسجلة لهذا السيريال
                </div>
              ) : (
                <div className="space-y-3 max-h-64 overflow-y-auto">
                  {timeline.map((item) => {
                    const typeInfo = getMovementTypeLabel(item.type);
                    const typeLabel = getTypeLabel(item);
                    return (
                      <div
                        key={item.id}
                        className="p-3 rounded-lg border border-gray-200 bg-gray-50/50 flex flex-col gap-2"
                      >
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className={`px-2 py-0.5 rounded text-xs font-medium ${typeInfo.color}`}>
                            {getMovementIcon(item.type)} {typeLabel}
                          </span>
                          <DateTimeDisplay
                            dateString={item.performedAt}
                            dateClassName="text-xs font-medium text-gray-700"
                            timeClassName="text-xs text-gray-500"
                          />
                        </div>
                        {hasFromOrTo(item) && (
                          <div className="text-sm text-gray-600 space-y-0.5">
                            {item.fromLocationName?.trim() && item.fromLocationName !== '-' && (
                              <div>من: {item.fromLocationName}</div>
                            )}
                            {item.toLocationName?.trim() && item.toLocationName !== '-' && (
                              <div>إلى: {item.toLocationName}</div>
                            )}
                          </div>
                        )}
                        <div className="text-xs text-gray-500">{item.performedByName}</div>
                        {item.notes?.trim() && (
                          <div className="text-xs text-gray-500 mt-1">{item.notes}</div>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
              {!timelineLoading && !timelineError && timeline.length > 0 && timelineHasMore && (
                <Button
                  variant="outline"
                  size="small"
                  onClick={loadMoreTimeline}
                  disabled={timelineLoadMore}
                  className="w-full mt-3 flex items-center justify-center gap-2"
                >
                  {timelineLoadMore ? INVENTORY_LOADING_TEXT : 'تحميل المزيد'}
                </Button>
              )}
            </>
          )}
        </div>

        {/* CTA */}
        <div className="pt-4 border-t border-gray-200">
          <Button
            variant="primary"
            onClick={() => {
              onViewInMovements(serial);
              onClose();
            }}
            className="w-full flex items-center justify-center gap-2"
          >
            <MapPin className="w-4 h-4" />
            عرض في حركة المخزون
          </Button>
        </div>
      </div>
    </Modal>
  );
};

export default SerialDetailsModal;
